import React, { useState, useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import LiteYouTubeEmbed from 'react-lite-youtube-embed';
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { FiArrowRight, FiCheckCircle, FiLayout, FiLink, FiShield, FiStar, FiZap, FiGlobe, FiCpu, FiYoutube, FiChevronDown } from 'react-icons/fi';

const FeatureCard = ({ icon, title, description, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay }}
    className="p-8 rounded-3xl bg-zinc-900/40 backdrop-blur-xl border border-orange-500/10 hover:border-orange-500/30 transition-all duration-500 group shadow-[0_8px_30px_rgba(234,88,12,0.02)] hover:shadow-[0_8px_30px_rgba(234,88,12,0.08)]"
  >
    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500/20 to-yellow-500/20 border border-orange-500/30 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500 shadow-[0_0_20px_rgba(234,88,12,0.2)]">
      <SafeIcon icon={icon} className="text-2xl text-orange-400" />
    </div>
    <h3 className="text-xl font-bold text-amber-50 mb-3 tracking-wide">{title}</h3>
    <p className="text-zinc-400 leading-relaxed font-medium">{description}</p>
  </motion.div>
);

const Video3DCard = () => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const xSpring = useSpring(x, { stiffness: 300, damping: 30 });
  const ySpring = useSpring(y, { stiffness: 300, damping: 30 });
  const rotateX = useTransform(ySpring, [-0.5, 0.5], ["15deg", "-15deg"]);
  const rotateY = useTransform(xSpring, [-0.5, 0.5], ["-15deg", "15deg"]);

  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const xPct = mouseX / width - 0.5;
    const yPct = mouseY / height - 0.5;
    x.set(xPct);
    y.set(yPct);
  };
  
  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <div style={{ perspective: "1000px" }} className="w-full max-w-[320px] mx-auto z-20">
      <motion.div
        ref={ref}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="relative w-full aspect-[9/16] rounded-[2rem] overflow-hidden shadow-[0_20px_50px_rgba(234,88,12,0.2)] border border-orange-500/30 bg-zinc-900"
      >
        <div className="absolute inset-0 w-full h-full pointer-events-auto">
          <LiteYouTubeEmbed
            id="48WqGSNEAyw"
            title="YouTube Short Testimonial"
            params="rel=0&modestbranding=1"
            wrapperClass="yt-lite h-full w-full absolute inset-0"
          />
        </div>
        {/* Glossy reflection overlay to enhance 3D feel */}
        <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent pointer-events-none rounded-[2rem]"></div>
      </motion.div>
    </div>
  );
};

const PricingCard = ({ title, price, desc, features, isPro }) => (
  <div className={`p-10 rounded-3xl backdrop-blur-xl relative overflow-hidden transition-all duration-500 flex flex-col h-full ${isPro ? 'bg-gradient-to-b from-orange-500/10 to-[#0a0500] border border-orange-500/30 shadow-[0_0_40px_rgba(234,88,12,0.15)] hover:shadow-[0_0_60px_rgba(234,88,12,0.25)]' : 'bg-zinc-900/40 border border-white/5 hover:border-white/10 shadow-lg'}`}>
    {isPro && <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/15 blur-[80px] pointer-events-none"></div>}
    
    <div className="relative z-10">
      <h3 className="text-2xl font-bold text-amber-50 mb-2">{title}</h3>
      <p className="text-sm text-zinc-400 mb-6">{desc}</p>
      
      <div className="mb-8 flex items-baseline">
        <span className="text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-amber-100 to-orange-300">{price}</span>
        {price !== 'Free' && <span className="text-zinc-500 text-sm ml-2 font-medium">/month</span>}
      </div>
      
      <ul className="space-y-4 mb-10 flex-1">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-3 text-zinc-300 text-sm font-medium">
            <SafeIcon icon={FiCheckCircle} className={`mt-0.5 text-lg flex-shrink-0 ${isPro ? 'text-orange-400' : 'text-zinc-500'}`} />
            <span className="leading-relaxed">{f}</span>
          </li>
        ))}
      </ul>
      
      <div className="mt-auto">
        <Link to="/auth">
          <Button variant={isPro ? 'primary' : 'secondary'} className="w-full !py-4 shadow-none text-base">
            Get Started
          </Button>
        </Link>
      </div>
    </div>
  </div>
);

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-orange-500/10 last:border-b-0">
      <button 
        onClick={() => setOpen(!open)} 
        className="w-full text-left py-6 flex justify-between items-center group outline-none"
      >
        <span className="text-lg text-amber-50 font-medium tracking-wide group-hover:text-orange-400 transition-colors pr-8">{q}</span>
        <SafeIcon 
          icon={FiChevronDown} 
          className={`text-zinc-500 text-xl transition-transform duration-300 flex-shrink-0 ${open ? 'rotate-180 text-orange-400' : ''}`} 
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }} 
            animate={{ height: 'auto', opacity: 1 }} 
            exit={{ height: 0, opacity: 0 }} 
            className="overflow-hidden"
          >
            <p className="pb-6 text-zinc-400 leading-relaxed font-medium">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Landing = () => {
  const faqs = [
    { q: "What is Moment of Truth?", a: "An enterprise-grade social proof platform that helps SaaS companies curate, manage, and embed premium customer testimonials beautifully." },
    { q: "How does the YouTube import work?", a: "Simply paste a YouTube video or Shorts link, and our AI instantly extracts the metadata, thumbnail, and embeds the video securely into your wall of love." },
    { q: "Do I need coding skills to embed widgets?", a: "Not at all. You just copy a single line of code and paste it into your website builder (Notion, Webflow, Framer, React, etc.)." },
    { q: "Can I use it on multiple websites?", a: "Yes, once you generate a widget, you can embed the iframe script on as many domains or pages as you like." },
    { q: "What happens if I cancel my Pro plan?", a: "You'll be downgraded to the Free plan. Only your first 25 published reviews will remain visible, and you'll lose access to premium branding options." }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-[#0a0500] selection:bg-orange-500/30 selection:text-amber-100">
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden px-6">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-orange-600/15 blur-[120px] rounded-full pointer-events-none"></div>
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-orange-500/10 to-yellow-500/10 border border-orange-500/20 text-orange-400 text-sm font-bold tracking-widest mb-8 uppercase shadow-[0_0_20px_rgba(234,88,12,0.1)]">
              <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse shadow-[0_0_8px_rgba(234,88,12,0.8)]"></span>
              The New Standard in Social Proof
            </div>
            
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-8 leading-[1.1] text-amber-50">
              Turn Customer Love into <br className="hidden md:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 via-yellow-400 to-orange-300">
                Enterprise Trust
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed font-medium">
              Curate, manage, and embed premium testimonials that match your brand's aesthetic. No coding required. Built for high-converting SaaS.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
              <Link to="/auth" className="w-full sm:w-auto">
                <Button variant="primary" className="w-full sm:w-auto px-10 py-4 text-lg shadow-[0_0_30px_rgba(234,88,12,0.3)] hover:shadow-[0_0_40px_rgba(234,88,12,0.5)]">
                  Get Started <SafeIcon icon={FiArrowRight} className="ml-2" />
                </Button>
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-6 text-sm text-zinc-500 font-medium">
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiCheckCircle} className="text-orange-500" /> No credit card required
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiCheckCircle} className="text-orange-500" /> Start for free
              </div>
              <div className="flex items-center gap-2">
                <SafeIcon icon={FiCheckCircle} className="text-orange-500" /> Cancel anytime
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Grid Section */}
      <section className="py-24 relative z-10 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-amber-50 mb-6 tracking-tight">Everything you need to build trust</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto text-lg font-medium">A suite of premium tools designed to help you aggregate and display social proof beautifully.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard 
              icon={FiLink} 
              title="Auto-Sync Integrations" 
              description="Automatically pull in the latest reviews from Google, Trustpilot, G2, and Clutch so your page is always up to date." 
              delay={0.1} 
            />
            <FeatureCard 
              icon={FiLayout} 
              title="Stunning Widgets" 
              description="Design beautiful, high-converting masonry grids and carousels that perfectly match your website's premium aesthetic." 
              delay={0.2} 
            />
            <FeatureCard 
              icon={FiGlobe} 
              title="Hosted Wall of Love" 
              description="Get a dedicated, SEO-optimized public page to showcase all your best testimonials in one immersive experience." 
              delay={0.3} 
            />
            <FeatureCard 
              icon={FiShield} 
              title="Verified Badges" 
              description="Build instant credibility with verified business and review badges that prove your commitment to authenticity." 
              delay={0.4} 
            />
            <FeatureCard 
              icon={FiStar} 
              title="Manual Curation" 
              description="Hand-pick and manually add bespoke testimonials from high-profile clients, complete with custom avatars and logos." 
              delay={0.5} 
            />
            <FeatureCard 
              icon={FiZap} 
              title="Lightning Fast" 
              description="Our lightweight widgets load instantly without slowing down your site, ensuring a seamless experience for your visitors." 
              delay={0.6} 
            />
          </div>
        </div>
      </section>

      {/* YouTube & AI Integration Section */}
      <section className="py-24 relative z-10 px-6 bg-zinc-900/20 border-y border-orange-500/10 overflow-hidden">
        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[600px] h-[600px] bg-yellow-500/10 blur-[150px] pointer-events-none rounded-full"></div>
        
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative z-10"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-orange-500/10 to-yellow-500/10 border border-orange-500/20 text-orange-400 text-xs font-bold tracking-widest mb-6 uppercase shadow-[0_0_15px_rgba(234,88,12,0.1)]">
                <SafeIcon icon={FiYoutube} className="text-lg" />
                <span className="mx-1">•</span>
                <SafeIcon icon={FiCpu} className="text-lg" />
                AI Powered Data Curation
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-amber-50 mb-6 tracking-tight leading-tight">
                Turn YouTube Shorts into <br/>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-yellow-400">High-Converting Assets</span>
              </h2>
              
              <p className="text-lg text-zinc-400 mb-8 leading-relaxed font-medium">
                Don't let your best video testimonials get lost in the feed. Smart Import all your Youtube testimonial videos with one click. Our AI automatically extracts metadata, identifies the speaker, and embeds the video flawlessly into your premium widgets.
              </p>
              
              <ul className="space-y-4 mb-10">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0 mt-0.5 border border-orange-500/30">
                    <SafeIcon icon={FiCheckCircle} className="text-orange-400 text-sm" />
                  </div>
                  <span className="text-zinc-300 font-medium">Zero-configuration embedding</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0 mt-0.5 border border-orange-500/30">
                    <SafeIcon icon={FiCheckCircle} className="text-orange-400 text-sm" />
                  </div>
                  <span className="text-zinc-300 font-medium">Automatic speaker & company identification</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0 mt-0.5 border border-orange-500/30">
                    <SafeIcon icon={FiCheckCircle} className="text-orange-400 text-sm" />
                  </div>
                  <span className="text-zinc-300 font-medium">Lightning-fast iframe loading</span>
                </li>
              </ul>
              
              <Link to="/dashboard/connect">
                <Button variant="primary" className="px-8 py-4 text-base shadow-[0_0_30px_rgba(234,88,12,0.3)]">
                  Start Import <SafeIcon icon={FiArrowRight} className="ml-2" />
                </Button>
              </Link>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }} 
              whileInView={{ opacity: 1, scale: 1 }} 
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-tr from-orange-500/5 to-yellow-500/5 rounded-full blur-3xl pointer-events-none"></div>
              <Video3DCard />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 relative z-10 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-sm font-bold tracking-widest text-orange-500 uppercase mb-3">Simple Pricing</h2>
            <h3 className="text-3xl md:text-5xl font-bold text-amber-50 mb-6 tracking-tight">Invest in transparency</h3>
            <p className="text-zinc-400 max-w-2xl mx-auto text-lg font-medium">Start building trust for free. Upgrade when you need more power and customization.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <PricingCard
              title="Free Forever"
              price="Free"
              desc="Generous enough to run your whole Wall of Love. Seriously."
              features={[
                "Sync reviews from Google, YouTube, Trustpilot & Clutch",
                "Publish up to 25 reviews",
                "Hosted review wall at moment.reviews",
                "Grid & Masonry layouts",
                "1 embeddable widget",
                "Verified business & review badges"
              ]}
              isPro={false}
            />
            <PricingCard
              title="Moment Pro"
              price="$21"
              desc="For teams whose reviews are their best salesperson."
              features={[
                "Everything in Free",
                "Unlimited published reviews",
                "All 11 wall layouts — timelines, walls, marquees & more",
                "Unlimited widgets",
                "No Moment of Truth branding on your wall or widgets",
                "Priority support",
                "Custom domain, auto-sync & analytics — rolling out to Pro first"
              ]}
              isPro={true}
            />
          </div>

          <p className="text-center text-sm text-zinc-500 mt-8">
            Prefer yearly? Pro is <span className="text-orange-400 font-semibold">$210/year</span> — two months free.
          </p>
        </div>
      </section>

      {/* How it Works Section */}
      <section className="py-24 relative z-10 px-6 bg-zinc-900/20 border-y border-orange-500/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-amber-50 mb-6 tracking-tight">How it works</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto text-lg font-medium">Three simple steps to elevate your brand's credibility.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 relative">
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-orange-500/0 via-orange-500/20 to-orange-500/0 z-0"></div>

            <div className="relative z-10 flex flex-col items-center text-center">
              <div className="w-24 h-24 rounded-full bg-[#0a0500] border-2 border-orange-500/30 flex items-center justify-center text-3xl font-extrabold text-orange-400 mb-6 shadow-[0_0_30px_rgba(234,88,12,0.2)]">1</div>
              <h3 className="text-xl font-bold text-amber-50 mb-3 tracking-wide">Connect Sources</h3>
              <p className="text-zinc-400 font-medium leading-relaxed max-w-xs">Link your existing review platforms or manually add your best customer quotes.</p>
            </div>

            <div className="relative z-10 flex flex-col items-center text-center">
              <div className="w-24 h-24 rounded-full bg-[#0a0500] border-2 border-orange-500/30 flex items-center justify-center text-3xl font-extrabold text-orange-400 mb-6 shadow-[0_0_30px_rgba(234,88,12,0.2)]">2</div>
              <h3 className="text-xl font-bold text-amber-50 mb-3 tracking-wide">Design & Curate</h3>
              <p className="text-zinc-400 font-medium leading-relaxed max-w-xs">Customize the look and feel of your widgets and select which reviews to highlight.</p>
            </div>

            <div className="relative z-10 flex flex-col items-center text-center">
              <div className="w-24 h-24 rounded-full bg-[#0a0500] border-2 border-orange-500/30 flex items-center justify-center text-3xl font-extrabold text-orange-400 mb-6 shadow-[0_0_30px_rgba(234,88,12,0.2)]">3</div>
              <h3 className="text-xl font-bold text-amber-50 mb-3 tracking-wide">Embed Anywhere</h3>
              <p className="text-zinc-400 font-medium leading-relaxed max-w-xs">Copy a single line of code and paste it directly into your website, Notion, or Webflow.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 relative z-10 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-amber-50 mb-6 tracking-tight">Frequently Asked Questions</h2>
            <p className="text-zinc-400 text-lg font-medium">Everything you need to know about the product and billing.</p>
          </div>
          
          <div className="bg-zinc-900/30 border border-orange-500/10 rounded-3xl p-6 md:p-10 backdrop-blur-xl shadow-xl">
            {faqs.map((faq, index) => (
              <FAQItem key={index} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="pb-32 pt-10 relative z-10 px-6">
        <div className="max-w-4xl mx-auto text-center p-12 md:p-16 rounded-3xl bg-gradient-to-br from-orange-500/10 to-yellow-500/5 border border-orange-500/20 relative overflow-hidden shadow-[0_0_50px_rgba(234,88,12,0.1)]">
          <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/20 blur-[100px] pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-yellow-500/10 blur-[100px] pointer-events-none"></div>

          <div className="relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold text-amber-50 mb-6 tracking-tight">Ready to build unwavering trust?</h2>
            <p className="text-xl text-zinc-400 mb-10 max-w-2xl mx-auto font-medium">Join the premium social proof platform designed for high-end SaaS companies.</p>
            <Link to="/auth">
              <Button variant="primary" className="px-10 py-5 text-lg shadow-[0_0_30px_rgba(234,88,12,0.3)] hover:shadow-[0_0_40px_rgba(234,88,12,0.5)]">
                Get Started Now
              </Button>
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Landing;